import { FC, PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { HydratedCallRecord } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { CallIntelTypes } from '@frontend/api-call-intel';
import {
  CIBannerEnum,
  CIBannerPriorityGroupEnum,
  CallAnalysis,
  CallAnalyzeStatus,
  CallFlagsData,
  CallInsightBaseProps,
  generateTrackingId,
} from '@frontend/call-intel-core';
import { TSSRecoveredOpportunityBanner, TSSUnscheduledBanner } from '@frontend/high-value-adoption';
import { useTranslation } from '@frontend/i18n';
import { IconName } from '@frontend/icons';
import { PriorityRendererItemWrapper } from '@frontend/priority-renderer';
import { theme } from '@frontend/theme';
import { Accordion, Button, Tabs, contextFactory } from '@frontend/design-system';
import { CallInsightAccordionType } from '../../types';
import { CallActivity, CallDetailsList } from './call-details-section';

interface CallBreakdownAccordionContextValue extends CallInsightBaseProps {
  hydratedCall: HydratedCallRecord;
  callFlags: CallFlagsData;
  hasCallIntelAccess: boolean;
}

enum CallBreakdownTabEnum {
  ConversationAnalysisTab = 'conversationAnalysis',
  CallDetailsTab = 'callDetails',
  CallActivityTab = 'callActivity',
}

interface CallBreakdownAccordionRootProps extends CallBreakdownAccordionContextValue {
  value?: CallInsightAccordionType | null;
}

interface CallBreakdownAccordionNamespace extends FC<PropsWithChildren<CallBreakdownAccordionRootProps>> {
  Header: typeof CallBreakdownAccordionHeader;
  Body: typeof CallBreakdownAccordionBody;
}

const [CallBreakdownAccordionContext, useCallBreakdownAccordionContext] =
  contextFactory<CallBreakdownAccordionContextValue>(
    'useCallBreakdownAccordionContext must be used within CallBreakdownAccordion'
  );

export const CallBreakdownAccordion: CallBreakdownAccordionNamespace = ({ children, value, ...props }) => {
  return (
    <CallBreakdownAccordionContext.Provider value={props}>
      <Accordion.Item
        value={value || 'callBreakdown'}
        data-testid='call-breakdown-accordion'
        trackingId={generateTrackingId({
          prefix: 'call-insight-panel',
          component: 'call-breakdown-accordion',
          isDemoAccount: props.isDemoAccount,
        })}
      >
        {children}
      </Accordion.Item>
    </CallBreakdownAccordionContext.Provider>
  );
};

const CallBreakdownAccordionHeader: FC<{
  title?: string;
  iconName?: IconName;
  onViewCallPath?: (call: HydratedCallRecord) => void;
}> = ({ title, iconName, onViewCallPath }) => {
  const { t } = useTranslation('call-insight-panel');

  const { hydratedCall } = useCallBreakdownAccordionContext();
  const isOutboundCall = hydratedCall.direction === 'outbound';

  const showViewCallPathButton = !isOutboundCall && !!onViewCallPath;
  const headerTitle = title || t('Call Breakdown');

  return (
    <Accordion.Header
      title={iconName ? undefined : headerTitle}
      css={styles.accordionHeader}
      data-testid='call-breakdown-header'
    >
      {iconName && <Accordion.Header.Icon icon={iconName} title={headerTitle} />}
      {showViewCallPathButton && (
        <Button
          iconName='swap-small'
          variant='tertiary'
          size='small'
          data-testid='view-call-path-button'
          trackingId={generateTrackingId({
            prefix: 'call-insight-panel',
            component: 'call-breakdown-header',
            context: 'view-call-path',
          })}
          onClick={() => onViewCallPath(hydratedCall)}
        >
          {t('View Call Path')}
        </Button>
      )}
    </Accordion.Header>
  );
};

interface CallBreakdownAccordionBodyProps {
  onUpdateCall: (call: CallIntelTypes.CallRecord) => void;
  unsavedEditChangesConfig: CallIntelTypes.UnsavedEditChangesConfig;
  setUnsavedEditChangesConfig: (config: CallIntelTypes.UnsavedEditChangesConfig) => void;
}

const CallBreakdownAccordionBody: FC<CallBreakdownAccordionBodyProps> = ({
  onUpdateCall,
  unsavedEditChangesConfig,
  setUnsavedEditChangesConfig,
}) => {
  const { t } = useTranslation('call-insight-panel');
  const {
    call: callInsights,
    callMetadata,
    callFlags,
    hydratedCall,
    isLoading,
    isDemoAccount,
    hasCallIntelAccess,
  } = useCallBreakdownAccordionContext();
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedTab, setSelectedTab] = useState(CallBreakdownTabEnum.ConversationAnalysisTab);

  const { isVoicemailCall, isFailedCall } = callFlags;
  const showConversationAnalysisTab = hasCallIntelAccess && !isVoicemailCall && !isFailedCall;
  const showCallActivity = !!hydratedCall.phoneTrees && hydratedCall.phoneTrees.length > 0;
  const showOnlyCallDetails = !showConversationAnalysisTab && !showCallActivity;

  const handleTabChange = useCallback((tab: string) => {
    setSelectedTab(tab as CallBreakdownTabEnum);
  }, []);

  useEffect(() => {
    if (!showConversationAnalysisTab) {
      setSelectedTab(CallBreakdownTabEnum.CallDetailsTab);
    }
  }, [showConversationAnalysisTab]);

  if (showOnlyCallDetails) {
    return (
      <Accordion.Body css={styles.accordionBody} data-testid='call-breakdown-body'>
        <div css={styles.content}>
          <CallDetailsList call={hydratedCall} />
        </div>
      </Accordion.Body>
    );
  }

  return (
    <Accordion.Body css={styles.accordionBody} data-testid='call-breakdown-body'>
      <Tabs controlledTab={selectedTab} onChange={handleTabChange}>
        <Tabs.Bar fullWidth css={{ marginTop: theme.spacing(2) }}>
          {showConversationAnalysisTab && (
            <Tabs.Tab id={CallBreakdownTabEnum.ConversationAnalysisTab}>{t('Conversation Analysis')}</Tabs.Tab>
          )}
          <Tabs.Tab id={CallBreakdownTabEnum.CallDetailsTab}>{t('Call Details')}</Tabs.Tab>
          {showCallActivity && <Tabs.Tab id={CallBreakdownTabEnum.CallActivityTab}>{t('Call Activity')}</Tabs.Tab>}
        </Tabs.Bar>

        <Tabs.Panel controller={CallBreakdownTabEnum.ConversationAnalysisTab}>
          <div css={styles.content}>
            <CallAnalyzeStatus {...callFlags} type='summary' styles={{ margin: 0 }} />
            <PriorityRendererItemWrapper<typeof TSSRecoveredOpportunityBanner>
              itemName={CIBannerEnum.TSS_CALL_INSIGHTS}
              groupName={CIBannerPriorityGroupEnum.CALL_INSIGHTS_PANEL}
              closeFnProp={'onDismiss'}
            >
              <TSSRecoveredOpportunityBanner
                locationId={callInsights?.locationId}
                isDemo={isDemoAccount}
                callInsights={callInsights}
              />
            </PriorityRendererItemWrapper>
            <PriorityRendererItemWrapper<typeof TSSUnscheduledBanner>
              itemName={CIBannerEnum.TSS_CALL_INSIGHTS}
              groupName={CIBannerPriorityGroupEnum.CALL_INSIGHTS_PANEL}
              closeFnProp={'onDismiss'}
            >
              <TSSUnscheduledBanner
                locationId={callInsights?.locationId}
                isDemo={isDemoAccount}
                callInsights={callInsights}
              />
            </PriorityRendererItemWrapper>
            <CallAnalysis
              call={callInsights}
              callMetadata={callMetadata}
              callFlags={callFlags}
              isLoading={isLoading}
              isDemoAccount={isDemoAccount}
              isEditMode={isEditMode}
              onUpdateCall={onUpdateCall}
              unsavedEditChangesConfig={unsavedEditChangesConfig}
              setUnsavedEditChangesConfig={setUnsavedEditChangesConfig}
              setIsEditMode={setIsEditMode}
              emptyStateMessages={{
                title: t('No Conversation Analysis available'),
                subTitle: t('Please review the recording.'),
              }}
            />
          </div>
        </Tabs.Panel>

        <Tabs.Panel controller={CallBreakdownTabEnum.CallDetailsTab}>
          <div css={styles.content}>
            <CallDetailsList call={hydratedCall} />
          </div>
        </Tabs.Panel>

        <Tabs.Panel controller={CallBreakdownTabEnum.CallActivityTab}>
          <div css={styles.content}>
            <CallActivity phoneTrees={hydratedCall.phoneTrees} />
          </div>
        </Tabs.Panel>
      </Tabs>
    </Accordion.Body>
  );
};

CallBreakdownAccordion.Header = CallBreakdownAccordionHeader;
CallBreakdownAccordion.Body = CallBreakdownAccordionBody;

const styles = {
  accordionHeader: css`
    .header-container {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: ${theme.spacing(2, 0, 2, 2)};
    }
  `,
  accordionBody: css`
    padding: 0;
  `,
  content: css`
    display: flex;
    flex-direction: column;
    min-height: 100px;
    padding: ${theme.spacing(2)};
    gap: ${theme.spacing(2)};
  `,
  tabContentWithBanners: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `,
};
